import React, { useEffect, useState } from "react";

import { CookingTimeDto, ScheduleDto } from "services/menu/restModels";

import {
    COOKING_TIME_OPTIONS,
    cookingTimeToValue,
    valueToCookingTime,
} from "shared/lib/cookingTime";

import { useActiveLanguage } from "shared/hooks/useActiveLanguage";
import { useMenuSettings } from "shared/hooks/useMenuSettings";
import {
    localizedText,
    setLocalizedValue,
    trimLocalizedMap,
} from "shared/lib/localizedMap";
import {
    Button,
    LabeledField,
    LanguageSegmentedControl,
    Modal,
    Select,
    TabItem,
    Tabs,
    TextInput,
    ToggleSwitch,
} from "shared/ui";

import { ScheduleEditor } from "features/MenuItemEditor/components/ScheduleEditor";
import { OrderEntry, OrderList } from "features/MenuEditor/components/OrderList";
import { useMenuEditorContext } from "features/MenuEditor/context";
import { useAllCategoryItems } from "features/MenuEditor/hooks/useAllCategoryItems";

import { ActionsSpacer, Hint, LanguageRow, TabPanel, TabsRow } from "./styles";

type CategoryTab = "main" | "schedule" | "order";

const TABS: TabItem<CategoryTab>[] = [
    { key: "main", label: "Основное" },
    { key: "schedule", label: "Расписание" },
    { key: "order", label: "Порядок позиций" },
];

export const CategorySettingsDialog = () => {
    const state = useMenuEditorContext();
    const category = state.dialog.kind === "categorySettings" ? state.dialog.category : null;

    const [tab, setTab] = useState<CategoryTab>("main");
    const [name, setName] = useState<Record<string, string>>({});
    const [schedule, setSchedule] = useState<ScheduleDto | null>(null);
    const [cookingTime, setCookingTime] = useState<CookingTimeDto>({
        fromTime: null,
        toTime: null,
    });
    const [isInStopList, setIsInStopList] = useState<boolean>(false);
    const [ordered, setOrdered] = useState<OrderEntry[] | null>(null);

    // Языки меню нужны только этой форме — грузим их вместе с ней, а не на весь экран.
    const { settings } = useMenuSettings(state.menuId);
    const languages = settings?.availableLanguages.enabledLocales || [];
    const { activeLang, setActiveLang } = useActiveLanguage(state.locale, languages);

    const {
        items: categoryItems,
        loading: itemsLoading,
        error: itemsError,
    } = useAllCategoryItems(state.menuId, category?.id ?? 0, tab === "order");

    // Форма наполняется из открытой категории — на её id, а не на объекте: объект
    // пересоздаётся при каждом обновлении сайдбара и сбрасывал бы несохранённый ввод.
    useEffect(() => {
        if (!category) return;

        setTab("main");
        setName(category.name);
        setSchedule(category.schedule);
        setCookingTime(category.cookingTime);
        setIsInStopList(category.isInStopList);
        setOrdered(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [category?.id]);

    useEffect(() => {
        if (itemsLoading || itemsError) return;

        setOrdered((current) =>
            current ??
            categoryItems.map((item) => ({
                id: item.id,
                label: localizedText(item.name, state.locale),
            }))
        );
    }, [categoryItems, itemsLoading, itemsError, state.locale]);

    if (!category || !schedule) return null;

    const orderChanged =
        !!ordered &&
        (ordered.length !== categoryItems.length ||
            ordered.some((entry, index) => entry.id !== categoryItems[index]?.id));

    const submit = async () => {
        const saved = await state.mutations.updateCategory(category.id, {
            name: trimLocalizedMap(name),
            schedule,
            cookingTime,
            isInStopList,
        });

        if (!saved) return;

        // Порядок — отдельная ручка, поэтому шлём её только если его действительно меняли.
        if (orderChanged && ordered) {
            const reordered = await state.mutations.reorderItems(
                category.id,
                ordered.map((entry) => entry.id)
            );

            if (!reordered) return;
        }

        state.closeDialog();
    };

    const canSubmit =
        Object.values(name).some((value) => value.trim().length > 0) &&
        !state.mutations.saving;

    const renderOrderTab = () => {
        if (itemsLoading) return <Hint>Загружаем позиции…</Hint>;
        if (itemsError) return <Hint>Не удалось загрузить позиции категории</Hint>;

        return (
            <>
                <Hint>Перетащите позиции — в таком порядке их увидит гость.</Hint>
                <OrderList
                    entries={ordered || []}
                    onReorder={setOrdered}
                    emptyText="В категории пока нет позиций."
                />
            </>
        );
    };

    return (
        <Modal
            open
            onClose={state.closeDialog}
            title="Настройки категории"
            actions={
                <>
                    <Button
                        variant="outlined"
                        color="danger"
                        disabled={state.mutations.saving}
                        onClick={() => state.openDialog({ kind: "deleteCategory", category })}
                    >
                        Удалить категорию
                    </Button>
                    <ActionsSpacer />
                    <Button
                        variant="outlined"
                        color="neutral"
                        disabled={state.mutations.saving}
                        onClick={state.closeDialog}
                    >
                        Отмена
                    </Button>
                    <Button variant="contained" disabled={!canSubmit} onClick={submit}>
                        Сохранить
                    </Button>
                </>
            }
        >
            <TabsRow>
                <Tabs<CategoryTab> value={tab} tabs={TABS} onChange={setTab} />
            </TabsRow>

            {tab === "main" && (
                <TabPanel>
                    {languages.length > 1 && (
                        <LanguageRow>
                            <LanguageSegmentedControl
                                languages={languages}
                                activeLang={activeLang}
                                primaryLang={state.locale}
                                onChange={setActiveLang}
                                isMissing={(lang) => !name[lang]}
                            />
                        </LanguageRow>
                    )}

                    <LabeledField label="Название">
                        <TextInput
                            value={name[activeLang]}
                            onChange={(value) =>
                                setName(setLocalizedValue(name, activeLang, value))
                            }
                        />
                    </LabeledField>

                    <LabeledField label="Время приготовления">
                        <Select
                            value={cookingTimeToValue(cookingTime)}
                            options={COOKING_TIME_OPTIONS}
                            onChange={(value) => setCookingTime(valueToCookingTime(value))}
                        />
                    </LabeledField>

                    <ToggleSwitch
                        checked={isInStopList}
                        onChange={setIsInStopList}
                        label="Вся категория в стоп-листе"
                        hint={`Скроет категорию и все ${category.itemsCount} позиций от гостей`}
                    />
                </TabPanel>
            )}

            {tab === "schedule" && (
                <TabPanel>
                    <ToggleSwitch
                        checked={schedule.isEnabled}
                        onChange={(isEnabled) => setSchedule({ ...schedule, isEnabled })}
                        label="Показывать по расписанию"
                        hint="Вне указанных интервалов категория скрыта от гостей"
                    />
                    {schedule.isEnabled && (
                        <ScheduleEditor schedule={schedule} onChange={setSchedule} />
                    )}
                </TabPanel>
            )}

            {tab === "order" && <TabPanel>{renderOrderTab()}</TabPanel>}
        </Modal>
    );
};
